import React, { useRef, useState } from "react";
import clsx from "clsx";
import { Input, makeStyles, Popper } from "@material-ui/core";
import API from "../../../utils/axios";

import Downshift from "downshift";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import { connect } from "react-redux";
import { setProject } from "../../../store/actions/project";
import { setSolution } from "../../../store/actions/solution";
import poweredByGoogle from "../../../assets/images/icons/powered-by-google.png";
import { setLoadingUnscheduled } from "../../../store/actions/loading";
import { useSnackbar } from "notistack";
import { setInfoDialog } from "../../../store/actions/common";
import { remCalc } from "../../../utils/helpers";
import RenderSuggestion from "../../common/RenderSuggestion";
import { handleStopAdd } from "../../../store/actions/custom";

const useStyles = makeStyles(theme => ({
  container: {
    flexGrow: 1,

    "& .MuiListItem-root.Mui-selected": {
      backgroundColor: "#eee"
    }
  },
  paper: {
    zIndex: 1,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    boxShadow: `${theme.variables.boxShadow}, 0 -2px 4px rgba(0, 0, 0, 0.1)`,
    overflow: "hidden",
    marginTop: -2,
    paddingTop: theme.spacing(1)
  },
  inputWrapper: {
    "&:after": {
      opacity: 0
    },

    "&.Mui-focused:before": {
      borderBottom: "2px solid rgba(0, 0, 0, 0.87)"
    }
  },
  inputField: {
    textOverflow: "ellipsis",
    paddingBottom: theme.spacing(1.875)
  },
  suggestion: {
    textOverflow: "ellipsis",
    overflow: "hidden"
  },
  fontWeightMedium: {
    fontWeight: 500
  }
}));

function UnscheduledInput({
  projectId,
  setProject,
  mapBounds,
  handleStopAdd,
  hideInput,
  setInfoDialog,
  setLoadingUnscheduled
}) {
  const classes = useStyles();
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);

  const downshiftRef = useRef();

  const session = useRef(
    new window.google.maps.places.AutocompleteSessionToken()
  );

  const handleInputChange = value => {
    setOpen(true);

    setInputValue(value);

    // Get Google autocomplete data
    if (value) {
      const autocomplete = new window.google.maps.places.AutocompleteService();
      autocomplete.getPlacePredictions(
        {
          input: value,
          sessionToken: session.current,
          bounds: {
            north: mapBounds._northEast.lat,
            south: mapBounds._southWest.lat,
            west: mapBounds._southWest.lng,
            east: mapBounds._northEast.lng
          },
          types: ["address"]
        },
        function (results) {
          console.log(results);
          setSuggestions(results ? results : []);
        }
      );
    } else setSuggestions([]);

    // Highlight first item
    downshiftRef.current.setHighlightedIndex(0);
  };

  const inputFieldRef = useRef();

  const { enqueueSnackbar } = useSnackbar();

  const handleItemSelect = selectedItem => {
    setOpen(false);

    if (selectedItem && selectedItem.place_id) {
      setLoadingUnscheduled(true);

      // Get place details
      const service = new window.google.maps.places.PlacesService(
        window.document.createElement("div")
      );
      service.getDetails(
        {
          placeId: selectedItem.place_id,
          language: "en",
          fields: ["address_components", "geometry", "formatted_address"]
        },
        function (PlaceResult, PlacesServiceStatus) {
          const body = { address: PlaceResult, id: null };

          // Post service changes
          handleStopAdd(body, { track: false });

          hideInput();
        }
      );
    }
  };

  const onInputFocus = e => {
    setOpen(true);
    setAnchorEl(e.currentTarget);
    e.target.select();
  };

  const onInputBlur = () => {
    setOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e, downshiftKeyDown) => {
    if (e.keyCode === 13) {
      downshiftRef.current.selectHighlightedItem();
    } else if (e.keyCode === 27) {
      hideInput();
    } else downshiftKeyDown(e);
  };

  return (
    <Downshift
      onInputValueChange={handleInputChange}
      onSelect={selectedItem => handleItemSelect(selectedItem)}
      defaultHighlightedIndex={0}
      itemToString={item => (item ? item.straat : "")}
      id="downshift-simple"
      ref={downshiftRef}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        highlightedIndex,
        // inputValue,
        selectedItem
      }) => {
        const { onKeyDown } = getInputProps({
          placeholder: "Search for an address"
        });

        return (
          <form
            autoComplete="off"
            onSubmit={e => e.preventDefault()}
            className={classes.container}
          >
            {/* Input */}
            <Input
              ref={inputFieldRef}
              onChange={e => handleInputChange(e.target.value)}
              value={inputValue || ""}
              placeholder="Enter address"
              className={clsx(classes.inputWrapper)}
              inputProps={{
                className: classes.inputField,
                autoComplete: "off",
                autoFocus: "on",
                "data-cy": "extendable-addStopInput"
              }}
              onBlur={onInputBlur}
              onFocus={onInputFocus}
              onKeyDown={e => handleKeyDown(e, onKeyDown)}
              fullWidth
            />

            {/* Suggestions list */}
            <Popper
              open={isOpen}
              anchorEl={anchorEl}
              style={{ zIndex: 100000, marginLeft: -5 }}
            >
              <div style={{ width: remCalc(448), zIndex: 1500 }}>
                {isOpen && suggestions.length ? (
                  <Paper className={classes.paper} square>
                    {suggestions.map((suggestion, index) =>
                      RenderSuggestion({
                        suggestion,
                        index,
                        value: inputValue,
                        itemProps: getItemProps({ item: suggestion }),
                        highlightedIndex,
                        selectedItem
                      })
                    )}

                    {/* Powered by Google */}
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      pt={1}
                      pb={1.5}
                      px={1.5}
                    >
                      <img
                        src={poweredByGoogle}
                        height={14}
                        alt="Powered by Google"
                      />
                    </Box>
                  </Paper>
                ) : null}
              </div>
            </Popper>
          </form>
        );
      }}
    </Downshift>
  );
}

function mapStateToProps(state) {
  return {
    projectId: state.project.id,
    project: state.project,
    mapBounds: state.map.bounds
  };
}

export default connect(mapStateToProps, {
  setSolution,
  setProject,
  handleStopAdd,
  setInfoDialog,
  setLoadingUnscheduled
})(UnscheduledInput);
